import { View } from "react-native";
import {
  BRACKET_SIZE,
  BRACKET_THICKNESS,
  DIMMED_COLOR,
} from "../../../../utilities/barcode-scanner/constants";

const commonBarStyle = {
  position: "absolute",
  backgroundColor: DIMMED_COLOR,
} as const;

const commonBracketStyle = {
  position: "absolute",
  width: BRACKET_SIZE,
  height: BRACKET_SIZE,
  backgroundColor: "transparent",
  borderColor: "white",
} as const;

type ScannerPadding = string | number;

export interface BarcodeFocusOverlayProps {
  children?: React.ReactNode;
  /**
   * Percentage padding from horizontal edge of screen to scanner area
   */
  horizontalPadding: ScannerPadding;
  /**
   * Percentage padding from vertical edge of screen to scanner area
   */
  verticalPadding: ScannerPadding;
}

export function BarcodeFocusOverlay({
  children,
  horizontalPadding,
  verticalPadding,
}: BarcodeFocusOverlayProps) {
  const bracketProps = { horizontalPadding, verticalPadding };

  return (
    <View
      style={{
        height: "100%",
        width: "100%",
      }}
    >
      <TopBar {...{ horizontalPadding, verticalPadding }} />
      <RightBar {...{ horizontalPadding }} />
      <BottomBar {...{ horizontalPadding, verticalPadding }} />
      <LeftBar {...{ horizontalPadding }} />

      <UpperLeftBracket {...bracketProps} />
      <UpperRightBracket {...bracketProps} />
      <LowerRightBracket {...bracketProps} />
      <LowerLeftBracket {...bracketProps} />

      {children}
    </View>
  );
}

type HorizontalBarProps = {
  horizontalPadding: ScannerPadding;
};

type VerticalBarProps = HorizontalBarProps & {
  verticalPadding: ScannerPadding;
};

const TopBar = ({ horizontalPadding, verticalPadding }: VerticalBarProps) => (
  <View
    style={{
      ...commonBarStyle,
      right: horizontalPadding,
      left: horizontalPadding,
      top: 0,
      height: verticalPadding,
    }}
  />
);

const RightBar = ({ horizontalPadding }: HorizontalBarProps) => (
  <View
    style={{
      ...commonBarStyle,
      right: 0,
      top: 0,
      bottom: 0,
      width: horizontalPadding,
    }}
  />
);

const BottomBar = ({
  horizontalPadding,
  verticalPadding,
}: VerticalBarProps) => (
  <View
    style={{
      ...commonBarStyle,
      right: horizontalPadding,
      left: horizontalPadding,
      bottom: 0,
      height: verticalPadding,
    }}
  />
);

const LeftBar = ({ horizontalPadding }: HorizontalBarProps) => (
  <View
    style={{
      ...commonBarStyle,
      left: 0,
      top: 0,
      bottom: 0,
      width: horizontalPadding,
    }}
  />
);

type BracketProps = VerticalBarProps;

const UpperLeftBracket = ({
  horizontalPadding,
  verticalPadding,
}: BracketProps) => (
  <View
    style={{
      ...commonBracketStyle,
      borderLeftWidth: BRACKET_THICKNESS,
      borderTopWidth: BRACKET_THICKNESS,
      left: horizontalPadding,
      top: verticalPadding,
    }}
  />
);

const UpperRightBracket = ({
  horizontalPadding,
  verticalPadding,
}: BracketProps) => (
  <View
    style={{
      ...commonBracketStyle,
      borderRightWidth: BRACKET_THICKNESS,
      borderTopWidth: BRACKET_THICKNESS,
      right: horizontalPadding,
      top: verticalPadding,
    }}
  />
);

const LowerRightBracket = ({
  horizontalPadding,
  verticalPadding,
}: BracketProps) => (
  <View
    style={{
      ...commonBracketStyle,
      borderRightWidth: BRACKET_THICKNESS,
      borderBottomWidth: BRACKET_THICKNESS,
      right: horizontalPadding,
      bottom: verticalPadding,
    }}
  />
);

const LowerLeftBracket = ({
  horizontalPadding,
  verticalPadding,
}: BracketProps) => (
  <View
    style={{
      ...commonBracketStyle,
      borderLeftWidth: BRACKET_THICKNESS,
      borderBottomWidth: BRACKET_THICKNESS,
      left: horizontalPadding,
      bottom: verticalPadding,
    }}
  />
);
